import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Sparkles, Send, Activity, AlertTriangle, Layers, PackageX,
  Clock, Wallet, Gauge, ShieldCheck, RefreshCw, MessageSquare,
} from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, EmptyState } from "../lib/ui";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import api from "../lib/api";

const rp = (v) =>
  v == null ? "-" : "Rp " + Math.round(Number(v)).toLocaleString("id-ID");
const num = (v) =>
  v == null ? "—" : Number(v).toLocaleString("id-ID", { maximumFractionDigits: 2 });

// ---------------- Lightweight markdown renderer (bold / headings / lists) ----------------
function inlineMd(s, keyBase) {
  const parts = String(s).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={`${keyBase}-${i}`} className="font-bold text-ink">{p.slice(2, -2)}</strong>
    ) : (
      <span key={`${keyBase}-${i}`}>{p}</span>
    )
  );
}

function Markdown({ text }) {
  const lines = String(text || "").split("\n");
  const out = [];
  let list = [];
  const flush = (k) => {
    if (list.length) {
      out.push(
        <ul key={`ul-${k}`} className="my-1.5 ml-4 list-disc space-y-1">
          {list.map((l, i) => <li key={i}>{inlineMd(l, `li-${k}-${i}`)}</li>)}
        </ul>
      );
      list = [];
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.replace(/\s+$/, "");
    if (/^#{1,6}\s/.test(line)) {
      flush(idx);
      out.push(<p key={idx} className="mb-1 mt-2 font-heading text-sm font-bold text-ink">{inlineMd(line.replace(/^#{1,6}\s/, ""), `h-${idx}`)}</p>);
    } else if (/^\s*[-*]\s+/.test(line)) {
      list.push(line.replace(/^\s*[-*]\s+/, ""));
    } else if (/^\s*\d+[).]\s+/.test(line)) {
      list.push(line.replace(/^\s*\d+[).]\s+/, ""));
    } else if (line.trim() === "") {
      flush(idx);
      out.push(<div key={idx} className="h-1.5" />);
    } else {
      flush(idx);
      out.push(<p key={idx} className="my-0.5">{inlineMd(line, `p-${idx}`)}</p>);
    }
  });
  flush("end");
  return <div className="text-sm leading-relaxed text-ink">{out}</div>;
}

const STATUS_TONE = { CRITICAL: "danger", LOW: "warn", SAFE: "success" };

// ---------------- Inventory Health bucket card ----------------
function HealthCard({ id, icon: Icon, tone, title, hint, items, count, renderMetrics, t }) {
  const tones = {
    danger: "bg-danger-light text-danger-text",
    warn: "bg-warn-light text-warn-text",
    mute: "bg-stone-100 text-stone-600",
    info: "bg-blue-100 text-blue-700",
    success: "bg-success-light text-success-text",
  };
  return (
    <Card className="flex flex-col" data-testid={`ai-health-${id}`}>
      <div className="mb-3 flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-heading text-base font-bold text-ink">{title}</h3>
            <Badge tone={tone} data-testid={`ai-health-count-${id}`}>{count ?? items.length}</Badge>
          </div>
          {hint && <p className="text-xs text-ink-mute">{hint}</p>}
        </div>
      </div>
      {items.length === 0 ? (
        <p className="rounded-xl bg-stone-50 px-3 py-4 text-center text-xs text-ink-mute">{t("No items")}</p>
      ) : (
        <div className="-mx-1 space-y-1">
          {items.map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-2 rounded-lg px-1 py-1.5 hover:bg-stone-50">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{r.product_name}</p>
                <p className="text-[11px] text-ink-mute">{r.category || r.product_type}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5 text-right">{renderMetrics(r)}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

const Metric = ({ label, value, tone }) => (
  <div className="min-w-[64px]">
    <p className={`text-sm font-bold ${tone || "text-ink"}`}>{value}</p>
    <p className="text-[10px] uppercase tracking-wide text-ink-mute">{label}</p>
  </div>
);

// ---------------- Inventory Health tab ----------------
function InventoryHealth() {
  const { t } = useT();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai/inventory-health");
      setData(data);
    } catch {
      toast.error(t("Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div>;
  if (!data) return null;

  const c = data.counts || {};
  return (
    <div className="space-y-4" data-testid="ai-health-grid">
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-soft">
          {t("Based on")} {c.tracked_products ?? 0} {t("tracked products")} · {t("usage window")} {data.thresholds?.usage_window_days}{t("d")}
        </p>
        <Button variant="outline" size="sm" onClick={load} data-testid="ai-health-refresh"><RefreshCw size={14} /> {t("Refresh")}</Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <HealthCard
          id="near_out_of_stock" icon={AlertTriangle} tone="danger" t={t}
          title={t("Near Out of Stock")} hint={t("At/below minimum or < 7 days cover")}
          items={data.near_out_of_stock || []} count={c.near_out_of_stock}
          renderMetrics={(r) => (<>
            <Metric label={t("Stock")} value={`${num(r.current_stock)} ${r.unit || ""}`} tone="text-danger-text" />
            <Metric label={t("Days")} value={r.days_remaining == null ? "—" : num(r.days_remaining)} />
          </>)}
        />
        <HealthCard
          id="overstock" icon={Layers} tone="warn" t={t}
          title={t("Overstock")} hint={t("Current well above ideal")}
          items={data.overstock || []} count={c.overstock}
          renderMetrics={(r) => (<>
            <Metric label={t("Stock")} value={`${num(r.current_stock)}`} tone="text-warn-text" />
            <Metric label={t("Ideal")} value={num(r.ideal_stock)} />
          </>)}
        />
        <HealthCard
          id="dead_stock" icon={PackageX} tone="mute" t={t}
          title={t("Dead Stock")} hint={t("No movement in 60 days")}
          items={data.dead_stock || []} count={c.dead_stock}
          renderMetrics={(r) => (<>
            <Metric label={t("Stock")} value={`${num(r.current_stock)} ${r.unit || ""}`} />
            <Metric label={t("Value")} value={rp(r.inventory_value)} />
          </>)}
        />
        <HealthCard
          id="slow_moving" icon={Clock} tone="info" t={t}
          title={t("Slow Moving")} hint={t("> 45 days of cover")}
          items={data.slow_moving || []} count={c.slow_moving}
          renderMetrics={(r) => (<>
            <Metric label={t("Days")} value={r.days_remaining == null ? "—" : num(r.days_remaining)} tone="text-blue-700" />
            <Metric label={t("Usage/d")} value={num(r.avg_daily_usage)} />
          </>)}
        />
        <HealthCard
          id="highest_inventory_value" icon={Wallet} tone="success" t={t}
          title={t("Highest Inventory Value")} hint={t("By Rupiah value")}
          items={data.highest_inventory_value || []}
          renderMetrics={(r) => (<>
            <Metric label={t("Value")} value={rp(r.inventory_value)} tone="text-success-text" />
            <Metric label={t("Stock")} value={`${num(r.current_stock)} ${r.unit || ""}`} />
          </>)}
        />
        <HealthCard
          id="inventory_coverage" icon={Gauge} tone="info" t={t}
          title={t("Inventory Coverage")} hint={t("Lowest days of cover first")}
          items={data.inventory_coverage || []}
          renderMetrics={(r) => (<>
            <Metric label={t("Days")} value={r.days_remaining == null ? "∞" : num(r.days_remaining)} tone="text-blue-700" />
            <Metric label={t("Usage/d")} value={num(r.avg_daily_usage)} />
          </>)}
        />
      </div>
    </div>
  );
}

// ---------------- Chat tab ----------------
function Chat() {
  const { t } = useT();
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/ai/suggestions").then(({ data }) => setSuggestions(data.groups || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = useCallback(async (question) => {
    const q = (question ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", { question: q, session_id: sessionRef.current });
      sessionRef.current = data.session_id;
      setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      let msg;
      if (detail) msg = detail;
      else if (e?.code === "ECONNABORTED") msg = t("The request took too long. Please try again.");
      else if (status) msg = `${t("Something went wrong")} (${status})`;
      else msg = t("Cannot reach the server. Please check your connection and try again.");
      toast.error(msg);
      setMessages((m) => [...m, { role: "assistant", text: msg }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, t, toast]);

  return (
    <div className="flex flex-col gap-4">
      {/* Read-only safety banner */}
      <div className="flex items-start gap-2.5 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3" data-testid="ai-readonly-banner">
        <ShieldCheck size={18} className="mt-0.5 shrink-0 text-blue-600" />
        <p className="text-xs text-blue-800">{t("This assistant only reads and analyzes data. It never makes changes, transfers, purchases, or production. Recommendations only.")}</p>
      </div>

      <Card className="mb-20 flex h-[calc(100vh-380px)] min-h-[420px] flex-col p-0 lg:mb-14" data-testid="ai-chat-card">
        {/* Messages */}
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-5 sm:px-6" data-testid="ai-messages">
          {messages.length === 0 && (
            <div className="mx-auto max-w-2xl py-2 text-center">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-light text-primary">
                <Sparkles size={26} />
              </div>
              <h3 className="font-heading text-lg font-bold text-ink">{t("Ask anything about your operations")}</h3>
              <p className="mt-1 text-sm text-ink-soft">{t("Inventory, production, sales, transfers, procurement and variance — answered from your real data.")}</p>
              <div className="mt-5 space-y-3 text-left">
                {suggestions.map((g, gi) => (
                  <div key={gi}>
                    <p className="mb-1.5 text-[11px] font-extrabold uppercase tracking-wider text-ink-mute">{g.label}</p>
                    <div className="flex flex-wrap gap-2">
                      {g.questions.map((qq, qi) => (
                        <button
                          key={qi}
                          data-testid={`ai-suggestion-${gi}-${qi}`}
                          onClick={() => send(qq)}
                          className="rounded-full border border-line bg-white px-3 py-1.5 text-left text-xs font-medium text-ink-soft transition-colors hover:border-primary/40 hover:bg-primary-light hover:text-primary"
                        >
                          {qq}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) =>
            m.role === "user" ? (
              <div key={i} className="flex justify-end" data-testid="ai-message-user">
                <div className="max-w-[85%] rounded-2xl rounded-br-md bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm">{m.text}</div>
              </div>
            ) : (
              <div key={i} className="flex gap-2.5" data-testid="ai-message-assistant">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary">
                  <Sparkles size={16} />
                </div>
                <div className="max-w-[88%] rounded-2xl rounded-tl-md border border-line bg-white px-4 py-3 shadow-sm">
                  <Markdown text={m.text} />
                </div>
              </div>
            )
          )}

          {loading && (
            <div className="flex gap-2.5" data-testid="ai-loading">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary">
                <Sparkles size={16} />
              </div>
              <div className="flex items-center gap-2 rounded-2xl rounded-tl-md border border-line bg-white px-4 py-3 text-sm text-ink-soft shadow-sm">
                <Spinner /> {t("AI is analyzing your data…")}
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-line bg-white p-3 sm:p-4">
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => { e.preventDefault(); send(); }}
          >
            <textarea
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder={t("Type your question… e.g. Produk apa yang hampir habis?")}
              className="cf-input max-h-28 flex-1 resize-none"
            />
            <Button type="submit" loading={loading} disabled={!input.trim()} data-testid="ai-send-button">
              <Send size={16} /> <span className="hidden sm:inline">{t("Send")}</span>
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}

// ---------------- Page ----------------
export default function AIAssistant() {
  const { t } = useT();
  const [tab, setTab] = useState("chat");

  const tabs = [
    { id: "chat", label: t("AI Assistant"), icon: MessageSquare },
    { id: "health", label: t("Inventory Health"), icon: Activity },
  ];

  return (
    <div data-testid="ai-assistant-page">
      <PageHeader title={t("AI Assistant")} subtitle={t("Read-only operations & inventory intelligence")} icon={Sparkles} />

      <div className="mb-5 inline-flex rounded-xl border border-line bg-white p-1">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            data-testid={`ai-tab-${tb.id}`}
            onClick={() => setTab(tb.id)}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
              tab === tb.id ? "bg-primary text-white shadow-sm" : "text-ink-soft hover:bg-stone-100"
            }`}
          >
            <tb.icon size={16} /> {tb.label}
          </button>
        ))}
      </div>

      {tab === "chat" ? <Chat /> : <InventoryHealth />}
    </div>
  );
}
